import { motion } from 'framer-motion';
import { useState, useRef, useEffect, ReactNode } from 'react';

interface AnimatedButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'dashboard';
  className?: string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}

export const AnimatedButton = ({
  children,
  onClick,
  variant = 'primary',
  className = '',
  type = 'button',
  disabled = false,
}: AnimatedButtonProps) => {
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current || disabled) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setRipples([...ripples, { x, y, id: Date.now() }]);

    setTimeout(() => {
      setRipples((prev) => prev.slice(1));
    }, 1000);

    onClick?.();
  };

  useEffect(() => {
    const handleMouseLeave = () => {
      setMousePosition({ x: 0, y: 0 });
    };

    const button = buttonRef.current;
    button?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      button?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const isPrimary = variant === 'primary';
  const isDashboard = variant === 'dashboard';

  const baseClasses =
    variant === 'primary'
      ? 'bg-gradient-to-r from-[#a855f7] to-[#3b82f6] text-white shadow-[0_10px_20px_-5px_rgba(168,85,247,0.3)]'
      : variant === 'dashboard'
        ? 'bg-gradient-to-r from-violet-600 to-blue-600 text-white shadow-lg shadow-violet-500/10'
        : variant === 'secondary'
          ? 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
          : variant === 'outline'
            ? 'border border-white/10 text-white hover:bg-white/5 transition-colors duration-300'
            : 'text-white hover:text-primary transition-colors duration-300 px-0 py-0';

  const layoutClasses = className.split(' ').filter(c =>
    /^(w-|m-|mt-|mb-|ml-|mr-|absolute|flex-|shrink-|grow-|top-|left-|right-|bottom-|z-)/.test(c)
  ).filter(c => !/^(bg-|shadow-|border-)/.test(c)).join(' ');

  return (
    <div className={`relative ${layoutClasses.includes('w-') ? '' : 'inline-block'} ${layoutClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      {/* Subtle Inner Glowing border pulse */}
      {isPrimary && !disabled && (
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{
            boxShadow: [
              '0 0 0px rgba(99, 102, 241, 0)',
              '0 0 12px rgba(99, 102, 241, 0.25)',
              '0 0 0px rgba(99, 102, 241, 0)',
            ],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}

      <motion.button
        ref={buttonRef}
        type={type}
        disabled={disabled}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        whileHover={disabled ? {} : { scale: 1.02 }}
        whileTap={disabled ? {} : { scale: 0.98 }}
        className={`relative overflow-hidden font-bold transition-all flex items-center justify-center gap-2 ${baseClasses} ${disabled ? 'pointer-events-none opacity-50' : ''} ${className.includes('px-') ? '' : 'px-12'} ${className.includes('py-') ? '' : 'py-4'} ${className.includes('rounded-') ? '' : 'rounded-full'} ${className}`}
      >
        {/* Magnet effect glow */}
        {!disabled && (
          <motion.div
            className="absolute inset-0 opacity-0 pointer-events-none rounded-full"
            animate={{
              opacity: mousePosition.x !== 0 ? (isPrimary || isDashboard ? 0.3 : 0.1) : 0,
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, ${isPrimary || isDashboard ? 'rgba(255, 255, 255, 0.4)' : 'rgba(99, 102, 241, 0.4)'}, transparent 50%)`,
            }}
            transition={{ duration: 0.2 }}
          />
        )}

        {/* Subtle shimmer effect */}
        {isPrimary && !disabled && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-full"
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatDelay: 4,
              ease: 'easeInOut',
            }}
          />
        )}

        {/* Ripple effects */}
        {ripples.map((ripple) => (
          <motion.span
            key={ripple.id}
            className={`absolute rounded-full pointer-events-none ${isPrimary ? 'bg-white/30' : 'bg-primary/20'}`}
            initial={{
              width: 0,
              height: 0,
              x: ripple.x,
              y: ripple.y,
              opacity: 1,
            }}
            animate={{
              width: 300,
              height: 300,
              x: ripple.x - 150,
              y: ripple.y - 150,
              opacity: 0,
            }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        ))}

        {/* Button text */}
        <span className="relative z-10 flex items-center justify-center gap-2 max-w-full font-semibold">{children}</span>
      </motion.button>
    </div>
  );
};